<template>
    <div class="section">
        <h2 class="subtitle is-size-3">Dine informationer</h2>

        <form @submit.prevent="submit">
            <h3 class="subtitle is-5">Personlige oplysninger</h3>

            <div class="columns">
                <div class="column">
                    <b-field :label="$t('global.forms.firstName')"
                             :type="{'is-danger': veeErrors.has('first_name')}"
                             :message="veeErrors.first('first_name')">
                        <b-input v-model="editableCustomer.first_name" name="first_name"
                                 v-validate="'required'" :data-vv-as="$t('global.forms.firstName')" />
                    </b-field>
                </div>

                <div class="column">
                    <b-field :label="$t('global.forms.lastName')"
                             :type="{'is-danger': veeErrors.has('last_name')}"
                             :message="veeErrors.first('last_name')">
                        <b-input v-model="editableCustomer.last_name" name="last_name"
                                 v-validate="'required'" :data-vv-as="$t('global.forms.lastName')" />
                    </b-field>
                </div>
            </div>

            <div class="columns">
                <div class="column is-6-desktop">
                    <b-field :label="$t('global.forms.phoneNumber')"
                             :type="{'is-danger': veeErrors.has('phoneNumber')}"
                             :message="veeErrors.first('phoneNumber')">
                        <b-input v-model="editableCustomer.phone_number" type="phone" name="phoneNumber"
                                 v-validate="'required|min:8'" :data-vv-as="$t('global.forms.phoneNumber')" />
                    </b-field>
                </div>
            </div>

            <h3 class="subtitle title is-5">Virksomhed</h3>
            <p class="subtitle is-6">Hvis du har virksomhed med CVR nummer, kan du indtaste det her. Så kan vi automatisk udfylde virsomhedens navn og adresse for dig.</p>

            <div class="columns">
                <div class="column">
                    <div class="field">
                        <b-field :label="$t('global.forms.company.vat')">
                            <b-input v-model="editableCustomer.vat_number" type="text" name="company_name" @input="onVatChange"/>
                        </b-field>
                    </div>
                </div>

                <div class="column">
                    <div class="field">
                        <b-field :label="$t('global.forms.company.name')">
                            <b-input v-model="editableCustomer.company_name" type="text" name="company_name"/>
                        </b-field>
                    </div>
                </div>
            </div>

            <b-notification v-if="unknownVat" type="is-info" :closable="false">
                CVR-nummeret kunne ikke bekræftes.
            </b-notification>

            <h3 class="subtitle is-5">Adresse</h3>


            <b-field :label="$t('global.forms.address')"
                     :type="{'is-danger': veeErrors.has('address')}"
                     :message="veeErrors.first('address')">
                <b-input v-model="editableCustomer.address" type="text" name="address"
                         v-validate="'required'" :data-vv-as="$t('global.forms.address')" />
            </b-field>

            <div class="columns">
                <div class="column">
                    <b-field :label="$t('global.forms.postcode')"
                             :type="{'is-danger': veeErrors.has('postcode')}"
                             :message="veeErrors.first('postcode')">
                        <b-input v-model="editableCustomer.postcode" type="text" name="postcode"
                                 v-validate="'required|min:4'" :data-vv-as="$t('global.forms.postcode')" />
                    </b-field>
                </div>

                <div class="column">
                    <b-field :label="$t('global.forms.city')"
                             :type="{'is-danger': veeErrors.has('city')}"
                             :message="veeErrors.first('city')">
                        <b-input v-model="editableCustomer.city" type="text" name="city"
                                 v-validate="'required'" :data-vv-as="$t('global.forms.city')" />
                    </b-field>
                </div>
            </div>

            <b-notification v-if="success" :active.sync="success" type="is-success">
                {{success}}
            </b-notification>

            <button type="submit" class="button is-primary" :class="{'is-loading': isSubmitting}" :disabled="veeErrors.any()">
                {{$t('global.actions.update')}}
            </button>
        </form>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import CustomerService from '@/taskflex/customers/services/CustomerService';
    import VatService from '@/taskflex/customers/services/VatService';

    export default {
        data() {
            return {
                editableCustomer: {
                    first_name: null,
                    last_name: null,
                    phone_number: null,

                    company_name: null,
                    vat_number: null,

                    address: null,
                    city: null,
                    postcode: null,
                },

                success: null,
                isSubmitting: false,

                isCheckingVatNumber: false,
                unknownVat: false,
            };
        },

        computed: {
            ...mapGetters('auth', [
                'currentUserAccount',
                'isCustomer',
            ]),
        },

        mounted() {
            this.editableCustomer = Object.assign(this.editableCustomer, this.currentUserAccount);
        },

        methods: {
            onVatChange(vatNumber) {
                this.unknownVat = false;

                if (vatNumber.length >= 2 && vatNumber.length <= 13) {
                    // Wait for typing to finish, or confirm via button
                    this.checkVat();
                }
            },

            checkVat() {
                this.isCheckingVatNumber = true;
                this.editableCustomer.company_name = '';

                const options = {
                    vatNumber: this.editableCustomer.vat_number,
                };

                VatService.check(options, ({ data: { data } }) => {
                    this.editableCustomer.company_name = data.name;

                    this.editableCustomer.address = data.address.address;
                    this.editableCustomer.postcode = data.address.postcode.toString();
                    this.editableCustomer.city = data.address.city;

                    this.isCheckingVatNumber = false;
                }, () => {
                    this.isCheckingVatNumber = false;
                    this.unknownVat = true;
                });
            },

            async submit() {
                if (!this.isCustomer || !await this.$validator.validateAll()) {
                    return;
                }

                this.success = null;

                const customer = this.editableCustomer;

                const options = {
                    first_name: customer.first_name,
                    last_name: customer.last_name,
                    phone_number: customer.phone_number,

                    company_name: customer.company_name,
                    vat_number: customer.vat_number,

                    address: customer.address,
                    city: customer.city,
                    postcode: customer.postcode,
                };

                this.isSubmitting = true;

                CustomerService.update(this.currentUserAccount.id, options, () => {
                    this.isSubmitting = false;

                    this.success = 'Dine oplysninger er blevet opdateret.';
                });
            },
        },
    };
</script>



// WEBPACK FOOTER //
// src/taskflex/customers/components/ContactInfoAccountArea.vue